import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import BriefingSection from "../components/BriefingSection";
import CampaignView from "../components/CampaignView";
import RatingPanel from "../components/RatingPanel";
import ChatSidebar from "../components/ChatSidebar";
import { api, getUserId } from "../lib/api";
import { toast } from "sonner";
import { Share2, Copy, MessagesSquare, Calendar, Target, Building2, Star, Presentation, ClipboardList, FileSpreadsheet } from "lucide-react";
import { SECTION_DISPLAY_ORDER } from "../lib/sectionOrder";

// Order comes from SECTION_DISPLAY_ORDER (single source of truth, shared with
// the share view and XLSX export) — this is only the display-name lookup for
// the tab strip. "_campaigns" isn't a real backend section, so it's appended
// after, same as before.
const SECTION_SHORT_LABELS = {
  account_overview: "Overview",
  what_is_working: "Working",
  worked_before_missing_now: "Worked Before",
  active_but_not_working: "Draining",
  structural_gaps: "Structural",
  new_campaign_opportunities: "Opportunities",
  product_feed: "Product Feed",
  next_steps: "Next Steps",
};

const SECTION_NAV = [
  ...SECTION_DISPLAY_ORDER.map((key) => ({ key, short: SECTION_SHORT_LABELS[key] })),
  { key: "_campaigns", short: "Campaigns" },
];

const GOAL_LABELS = {
  full_audit: "Full Audit",
  grow_revenue: "Grow Revenue",
  cut_waste: "Cut Waste",
  understand_history: "Understand History",
};

function formatCurrency(n) {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);
}

function formatNumber(n) {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(Number(n) || 0);
}

export default function BriefingPage() {
  const { briefingId } = useParams();
  const userId = getUserId();
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatContext, setChatContext] = useState({ section: null, finding: null });
  const [activeTab, setActiveTab] = useState("account_overview");
  const [rating, setRating] = useState(null);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [pitchLoading, setPitchLoading] = useState(false);
  const [strategyLoading, setStrategyLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const sections = briefing?.sections || [];

  const handlePitchDeck = async () => {
    setPitchLoading(true);
    try {
      const { content } = await api.pitchDeck(briefingId);
      const blob = new Blob([content], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${briefing.customer_name || "account"} - Pitch Deck.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Pitch deck generation failed. Please try again.");
    } finally {
      setPitchLoading(false);
    }
  };

  const handleExportXlsx = async () => {
    setExportLoading(true);
    try {
      const blob = await api.exportBriefingXlsx(briefingId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${briefing.customer_name || "account"} - Briefing.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleStrategy = async () => {
    setStrategyLoading(true);
    try {
      const { content } = await api.strategyPlan(briefingId);
      const blob = new Blob([content], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${briefing.customer_name || "account"} - 3-Week Strategy.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Strategy generation failed. Please try again.");
    } finally {
      setStrategyLoading(false);
    }
  };

  const handleRate = async () => {
    setRatingLoading(true);
    setRating(null);
    try {
      const result = await api.rateBriefing(briefingId);
      setRating(result);
    } catch (e) {
      toast.error("Rating failed. Please try again.");
    } finally {
      setRatingLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const b = await api.getBriefing(briefingId, userId);
        setBriefing(b);
      } catch (e) {
        setError(e?.response?.data?.detail || "Briefing not found.");
      } finally {
        setLoading(false);
      }
    })();
  }, [briefingId, userId]);

  const summary = briefing?.raw_summary || {};
  const campaigns = summary.campaigns_summary || {};

  const shareUrl = useMemo(() => {
    if (!briefing?.share_token) return null;
    return `${window.location.origin}/share/${briefing.share_token}`;
  }, [briefing]);

  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied.");
    } catch {
      toast.error("Copy failed. Long-press the link to copy.");
    }
  };

  const onAskAgent = (section, finding) => {
    setChatContext({ section, finding });
    setChatOpen(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="mx-auto max-w-5xl space-y-3 px-6 py-12 md:px-10">
          <div className="shimmer h-8 w-2/3 rounded-md" />
          <div className="shimmer h-5 w-1/3 rounded-md" />
          <div className="shimmer mt-6 h-32 w-full rounded-md" />
          <div className="shimmer h-32 w-full rounded-md" />
          <div className="shimmer h-32 w-full rounded-md" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl px-6 py-20 text-center md:px-10">
          <p className="font-heading text-2xl font-medium text-slate-900">{error}</p>
          <Link to="/" className="mt-4 inline-flex rounded-md bg-[#0052FF] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0040CC]" data-testid="home-link">
            Back home
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`mx-auto max-w-5xl px-6 py-10 md:px-10 md:py-16 ${chatOpen ? "md:pr-[440px]" : ""}`}>
        {/* Header */}
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">briefing</p>
            <h1 className="mt-2 font-heading text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
              {briefing.customer_name || "Untitled account"}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <Building2 size={12} strokeWidth={1.8} />
                <span className="num">{briefing.customer_id}</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={12} strokeWidth={1.8} />
                Last {briefing.lookback_days} days
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Target size={12} strokeWidth={1.8} />
                {GOAL_LABELS[briefing.goal] || briefing.goal}
              </span>
              <span>Generated {new Date(briefing.created_at).toLocaleString()}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={copyShare}
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-slate-300"
              data-testid="share-copy-btn"
            >
              <Share2 size={14} strokeWidth={1.8} />
              Copy share link
            </button>
            <button
              onClick={rating ? () => setRating(null) : handleRate}
              disabled={ratingLoading}
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-slate-300 disabled:opacity-60"
            >
              <Star size={14} strokeWidth={1.8} className={ratingLoading ? "animate-pulse text-amber-400" : "text-amber-400"} />
              {ratingLoading ? "Rating…" : rating ? "Hide rating" : "Rate analysis"}
            </button>
            <button
              onClick={handlePitchDeck}
              disabled={pitchLoading}
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-slate-300 disabled:opacity-60"
            >
              <Presentation size={14} strokeWidth={1.8} className={pitchLoading ? "animate-pulse text-[#0052FF]" : "text-[#0052FF]"} />
              {pitchLoading ? "Generating…" : "Pitch deck"}
            </button>
            <button
              onClick={handleStrategy}
              disabled={strategyLoading}
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-slate-300 disabled:opacity-60"
            >
              <ClipboardList size={14} strokeWidth={1.8} className={strategyLoading ? "animate-pulse text-emerald-500" : "text-emerald-500"} />
              {strategyLoading ? "Generating…" : "Strategy"}
            </button>
            <button
              onClick={handleExportXlsx}
              disabled={exportLoading}
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-slate-300 disabled:opacity-60"
            >
              <FileSpreadsheet size={14} strokeWidth={1.8} className={exportLoading ? "animate-pulse text-teal-600" : "text-teal-600"} />
              {exportLoading ? "Exporting…" : "Export all"}
            </button>
            <button
              onClick={() => { setChatContext({ section: null, finding: null }); setChatOpen(true); }}
              className="inline-flex items-center gap-2 rounded-md bg-[#0052FF] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#0040CC]"
              data-testid="open-chat-btn"
            >
              <MessagesSquare size={14} strokeWidth={1.8} />
              Ask agent
            </button>
          </div>
        </div>

        {/* Summary strip */}
        <div className="card-soft mt-8 grid grid-cols-2 gap-4 p-5 md:grid-cols-5">
          <SummaryStat label="Campaigns" value={formatNumber(campaigns.campaign_count)} sub={`${campaigns.active_count || 0} active`} />
          <SummaryStat label="Spend" value={formatCurrency(campaigns.total_spend)} />
          <SummaryStat label="Conversions" value={formatNumber(campaigns.total_conversions)} />
          <SummaryStat label="Conv. value" value={formatCurrency(campaigns.total_conversion_value)} />
          <SummaryStat
            label="ROAS"
            value={campaigns.total_spend > 0 ? `${(campaigns.total_conversion_value / campaigns.total_spend).toFixed(2)}x` : "—"}
          />
        </div>

        {/* Rating panel */}
        {rating && <RatingPanel rating={rating} onClose={() => setRating(null)} />}

        {/* Shareable link (if visible) */}
        {shareUrl && (
          <div className="mt-4 flex items-center gap-3 rounded-md border border-dashed border-slate-300 bg-white px-4 py-3 text-xs text-slate-600">
            <Copy size={12} strokeWidth={1.8} className="text-slate-400" />
            <span className="num truncate">{shareUrl}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="mt-8 border-b border-slate-200">
          <div className="flex overflow-x-auto scrollbar-none">
            {SECTION_NAV.map((nav, i) => {
              const isSpecial = nav.key.startsWith("_");
              const exists = isSpecial || sections.some(s => s.key === nav.key);
              if (!exists) return null;
              return (
                <button
                  key={nav.key}
                  onClick={() => setActiveTab(nav.key)}
                  className={`flex shrink-0 items-center gap-1.5 border-b-2 px-5 py-3 text-xs font-medium whitespace-nowrap transition-colors ${
                    activeTab === nav.key
                      ? "border-[#0052FF] text-[#0052FF]"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <span className="num text-[10px] text-slate-400">0{i + 1}</span>
                  {nav.short}
                </button>
              );
            })}
          </div>
        </div>

        {/* Active section */}
        <div className="mt-4">
          {activeTab === "_campaigns" ? (
            <CampaignView summary={briefing.raw_summary} sections={briefing.sections} />
          ) : (
            sections
              .filter(s => s.key === activeTab)
              .map(section => (
                <BriefingSection
                  key={section.key}
                  section={section}
                  // Numeral badge tracks this page's own tab order
                  // (SECTION_NAV, derived from SECTION_DISPLAY_ORDER), not
                  // raw storage-array position — keeps the badge correct
                  // regardless of how sections happen to be stored.
                  index={SECTION_NAV.findIndex(n => n.key === section.key)}
                  forceOpen
                  onAskAgent={onAskAgent}
                />
              ))
          )}
        </div>
      </div>

      <ChatSidebar
        briefingId={briefingId}
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        contextSection={chatContext.section}
        contextFinding={chatContext.finding}
      />
    </Layout>
  );
}

function SummaryStat({ label, value, sub }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="num mt-1 text-lg font-semibold text-slate-900">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-500">{sub}</p>}
    </div>
  );
}
